import { Component, inject, OnInit } from '@angular/core';
import { NgxPaymentResult, PaymentResult } from '@digipay/ngx-payment-result';
import { ActivatedRoute } from '@angular/router';
import { ReceiptService } from './receipt.service';
import { CommonModule } from '@angular/common';
import { takeUntil } from 'rxjs';
import { TransactionTypeEnum } from '../../../data-access/enums/transaction-type.enum';
import { OrderStatus } from '../../../data-access/enums/order-status';
import { IReceipt } from '../../../data-access/models/receipt.interface';
import { BaseComponent } from '../../../components/core/components/base/base.component';
import { ReceiptHandlerService } from '../../../components/core/services/receipt-handler.service';
import { WealthNavigationService } from '@client-monorepo/wealth/navigation';
import { PENDING_TRANSACTIONS_ROUTE, PORTFO, TRANSACTIONS_ROUTE, WALLETS_ROUTE } from '../../../data-access/constants/app-routes';
import { WalletService } from '../../../features/wallet/services/wallet.service';
import { NgxEventTrackerService } from '@digipay/ngx-event-tracker';
import { SpinnerComponent } from '../../components/spinner/spinner.component';
import { MessageService } from '@client-monorepo/common/utilities';

@Component({
  selector: 'app-receipt',
  standalone: true,
  imports: [CommonModule, NgxPaymentResult, SpinnerComponent],
  templateUrl: './receipt.component.html',
  styleUrl: './receipt.component.scss',
})
export class ReceiptComponent extends BaseComponent implements OnInit {
  info: IReceipt;
  isLoading = true;
  uniqueId = '';
  orderStatus = OrderStatus;

  paymentResult!: PaymentResult;

  transactionType = TransactionTypeEnum;
  navigationService = inject(WealthNavigationService);
  from: string | null = null;
  trackingCode: string;
  walletAction: string;

  private eventService = inject(NgxEventTrackerService);
  private messageService = inject(MessageService);

  constructor(
    private route: ActivatedRoute,
    private walletService: WalletService,
    private receiptService: ReceiptService,
    private receiptHandlerService: ReceiptHandlerService,
  ) {
    super();
    this.uniqueId = this.route.snapshot.queryParams['uniqueId'];
    this.trackingCode = this.route.snapshot.queryParams['trackingCode'];
    this.walletAction = this.route.snapshot.queryParams['walletAction'];
    this.from = this.route.snapshot.queryParams['from'];
  }

  ngOnInit(): void {
    this.getOrderData();
  }

  onBackClicked() {
    if (this.info.instrumentType === 'Wallet') {
      this.navigationService.navigate([WALLETS_ROUTE, this.info.walletName.toLocaleLowerCase()]);
    } else if (this.from === 'portfo') {
      this.navigationService.navigate([PORTFO]);
    } else if (this.from === 'pendingTransactions') {
      this.navigationService.navigate([PENDING_TRANSACTIONS_ROUTE]);
    } else {
      this.navigationService.navigate([TRANSACTIONS_ROUTE]);
    }
  }

  // TODO: Optimize this code
  private getOrderData() {
    if (this.trackingCode) {
      if (this.walletAction === 'cashOut') {
        this.walletService
          .walletCashoutInquiry(this.trackingCode)
          .pipe(takeUntil(this.destroyObservable))
          .subscribe((res) => {
            if (res.success) {
              this.info = res.result;
              this.receiptHandlerService._setReceiptData = this.info;
              const items = this.receiptHandlerService._setFields();
              this.paymentResult = {
                ...this.paymentResult,
                amount: this.info.amount || 0,
                items,
                paymentResult: this.receiptHandlerService._getReceiptInfo().result,
                title:
                  res.result.status === OrderStatus.Approved
                    ? `برداشت از ${res.result.instrumentName} انجام شد`
                    : `برداشت از ${res.result.instrumentName} در حال انجام است`,
              };
              const eventData = {
                eventName: `${res.result.walletName.toLowerCase()}-cashout-${res.result.status === OrderStatus.Approved ? 'success' : 'failure'}`,
                eventData: {
                  Status: this.info?.status,
                  TransactionType: this.info.transactionType,
                },
              };
              this.eventService.sendEvent(eventData, { platforms: ['gtm'] });
            }
            this.isLoading = false;
          });
      } else {
        this.walletService
          .walletCashinInquiry(this.trackingCode)
          .pipe(takeUntil(this.destroyObservable))
          .subscribe((res) => {
            if (res.success) {
              this.info = res.result;
              this.receiptHandlerService._setReceiptData = this.info;
              const items = this.receiptHandlerService._setFields();
              this.paymentResult = {
                ...this.paymentResult,
                amount: this.info.amount || 0,
                items,
                paymentResult: this.receiptHandlerService._getReceiptInfo().result,
                title:
                  res.result.status === OrderStatus.Approved
                    ? 'واریز به ' + res.result.instrumentName
                    : 'واریز به ' + res.result.instrumentName + ' انجام نشد',
              };

              const eventData = {
                eventName: `${res.result.walletName.toLowerCase()}-cashin-${res?.result?.status === OrderStatus.Approved ? 'success' : 'failure'}`,
                eventData: {
                  Status: this.info?.status,
                  TransactionType: this.info?.transactionType,
                },
              };
              this.eventService.sendEvent(eventData, { platforms: ['gtm'] });
              this.isLoading = false;
            } else {
              if (res && res.error && res.error.title) {
                this.messageService.showErrorMessage(res.error.title);
              }
            }
          });
      }
    } else {
      this.receiptService
        .getReceiptData(this.uniqueId)
        .pipe(takeUntil(this.destroyObservable))
        .subscribe((res) => {
          if (res?.success) {
            this.info = res.result;
            this.receiptHandlerService._setReceiptData = this.info;
            const items = this.receiptHandlerService._setFields();
            this.paymentResult = {
              ...this.paymentResult,
              amount: this.info.amount || 0,
              items,
              paymentResult: this.receiptHandlerService._getReceiptInfo().result,
              title: this.receiptHandlerService._getReceiptInfo().title,
              message: this.receiptHandlerService._generateWaitingMessage(),
            };
          }
          this.isLoading = false;
        });
    }
  }
}
